import React, { useEffect, useState, useCallback } from "react";
import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import "../Users/Users.scss";

// Admin "Web Guests" tab. People who booked a table or bought a ticket on
// getzona.app with no ZONA account, so no other tab can show them: they have no
// user doc. Each row opens the full record of what we sent them and what came
// back (server: /admin/web-guests, stored on the request or ticket itself).
//
// The header numbers are the thing ZONA promises venues: how many people
// started, and how many of those finished.

const fmt = (d) => {
  if (!d) return "—";
  try {
    return (
      new Date(d).toLocaleString("en-US", {
        timeZone: "America/New_York",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }) + " ET"
    );
  } catch {
    return "—";
  }
};

const money = (cents) =>
  typeof cents === "number" ? `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—";

const statusColor = (row) => {
  if (row.paid) return "#63D67A";
  if (String(row.status).startsWith("expired") || String(row.status).startsWith("refunded")) return "#ff8a8a";
  if (String(row.status).startsWith("unfinished")) return "#ffcf5c";
  return "rgba(255,255,255,.75)";
};

const TABS = [
  { key: "all", label: "All" },
  { key: "table", label: "Tables" },
  { key: "ticket", label: "Tickets" },
];

const WebGuests = () => {
  const [kind, setKind] = useState("all");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(null);

  const load = useCallback(async (which) => {
    setLoading(true);
    setErr(false);
    try {
      const { data } = await api.get("/admin/web-guests", { params: { kind: which, records: 100 } });
      setRows(data?.data || []);
      setSummary(data?.summary || null);
    } catch (e) {
      setErr(true);
      setRows([]);
      setSummary(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load(kind);
  }, [load, kind]);

  const cell = { padding: "10px 12px", borderBottom: "1px solid rgba(255,255,255,.08)", fontSize: 13, verticalAlign: "top" };
  const head = { ...cell, color: "rgba(255,255,255,.6)", fontWeight: 600, textAlign: "left", whiteSpace: "nowrap" };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content" style={{ padding: 24 }}>
          <h1 style={{ color: "#fff", marginBottom: 6 }}>Web Guests</h1>
          <p style={{ color: "rgba(255,255,255,.6)", marginBottom: 18, fontSize: 14 }}>
            Bookings and ticket purchases made on getzona.app with no ZONA account, and every
            message we sent them.
          </p>

          {summary && (
            <div style={{ display: "flex", gap: 24, marginBottom: 20, flexWrap: "wrap" }}>
              {[
                ["Started", summary.started],
                ["Finished", summary.finished],
                ["Tables", `${summary.tables_finished}/${summary.tables_started}`],
                ["Tickets", `${summary.tickets_finished}/${summary.tickets_started}`],
              ].map(([label, value]) => (
                <div key={label} style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)", borderRadius: 14, padding: "12px 18px", minWidth: 120 }}>
                  <div style={{ color: "rgba(255,255,255,.55)", fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em" }}>{label}</div>
                  <div style={{ color: "#fff", fontSize: 22, fontWeight: 800 }}>{value}</div>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setKind(t.key)}
                style={{
                  padding: "8px 16px",
                  borderRadius: 10,
                  border: "1px solid rgba(255,255,255,.15)",
                  background: kind === t.key ? "#4F30EA" : "transparent",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {loading && <p style={{ color: "rgba(255,255,255,.6)" }}>Loading…</p>}
          {err && <p style={{ color: "#ff8a8a" }}>Could not load web guests.</p>}
          {!loading && !err && rows.length === 0 && (
            <p style={{ color: "rgba(255,255,255,.6)" }}>Nobody has booked or bought on the web yet.</p>
          )}

          {!loading && !err && rows.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff", minWidth: 900 }}>
                <thead>
                  <tr>
                    <th style={head}>When</th>
                    <th style={head}>Type</th>
                    <th style={head}>Guest</th>
                    <th style={head}>Contact</th>
                    <th style={head}>Night</th>
                    <th style={head}>Amount</th>
                    <th style={head}>Status</th>
                    <th style={head}>Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <React.Fragment key={`${r.kind}-${r.id}`}>
                      <tr>
                        <td style={cell}>{fmt(r.created)}</td>
                        <td style={cell}>{r.kind === "table" ? "Table" : "Ticket"}</td>
                        <td style={cell}>
                          {r.name || "—"}
                          {r.id_verified && (
                            <div style={{ color: "#63D67A", fontSize: 11 }}>ID verified{r.id_name ? `: ${r.id_name}` : ""}</div>
                          )}
                        </td>
                        <td style={cell}>
                          <div>{r.phone || "—"}</div>
                          <div style={{ color: "rgba(255,255,255,.6)" }}>{r.email || "—"}</div>
                        </td>
                        <td style={cell}>
                          <div>{r.event || "—"}</div>
                          <div style={{ color: "rgba(255,255,255,.6)" }}>
                            {[r.venue, r.item].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </td>
                        <td style={cell}>{money(r.amount_cents)}</td>
                        <td style={{ ...cell, color: statusColor(r) }}>{r.status}</td>
                        <td style={cell}>
                          <button
                            type="button"
                            onClick={() => setOpen(open === `${r.kind}-${r.id}` ? null : `${r.kind}-${r.id}`)}
                            style={{ background: "none", border: 0, color: "#9b8bff", cursor: "pointer", textDecoration: "underline", fontSize: 13 }}
                          >
                            {r.messages.length} sent
                          </button>
                        </td>
                      </tr>
                      {open === `${r.kind}-${r.id}` && (
                        <tr>
                          <td style={{ ...cell, background: "rgba(255,255,255,.03)" }} colSpan={8}>
                            {r.messages.length === 0 && <span style={{ color: "rgba(255,255,255,.6)" }}>Nothing sent yet.</span>}
                            {r.messages.map((m, i) => (
                              <div key={i} style={{ marginBottom: 10 }}>
                                <div style={{ color: "rgba(255,255,255,.55)", fontSize: 12 }}>
                                  {fmt(m.at)} · {m.dir === "in" ? "from guest" : "to guest"} · {m.channel} · {m.kind}
                                  {m.error ? <span style={{ color: "#ff8a8a" }}> · failed: {m.error}</span> : ""}
                                </div>
                                {m.subject && <div style={{ fontWeight: 600 }}>{m.subject}</div>}
                                <div style={{ color: "rgba(255,255,255,.8)" }}>{m.body || ""}</div>
                              </div>
                            ))}
                            <div style={{ color: "rgba(255,255,255,.45)", fontSize: 12, marginTop: 8 }}>
                              {r.ip ? `IP ${r.ip}` : "No IP"} · {r.device ? r.device.slice(0, 90) : "No device"}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </div>
    </main>
  );
};

export default WebGuests;
